import { useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Reusable sortable table header hook + component
 * Usage:
 *   const { sortedData, SortHeader } = useSortableTable(data, 'name');
 *   <SortHeader field="name">Name</SortHeader>
 */
export function useSortableTable(data, defaultField = null, defaultDir = 'asc') {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortField) return 0;
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    // Handle nulls
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // Numeric comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // Date-like string comparison (YYYY-MM-DD or ISO)
    const datePattern = /^\d{4}-\d{2}/;
    if (datePattern.test(String(aVal)) && datePattern.test(String(bVal))) {
      return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    }
    
    // String comparison
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    if (sortDir === 'asc') return aStr.localeCompare(bStr);
    return bStr.localeCompare(aStr);
  });

  const SortHeader = ({ field, children, className = '' }) => {
    const isActive = sortField === field;
    return (
      <th 
        className={`text-left p-3 font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors ${className}`}
        onClick={() => toggleSort(field)}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {isActive ? (
            sortDir === 'asc' ? <ArrowUp size={12} className="text-primary" /> : <ArrowDown size={12} className="text-primary" />
          ) : (
            <ArrowUpDown size={12} className="opacity-30" />
          )}
        </span>
      </th>
    );
  };

  return { sortedData, SortHeader, sortField, sortDir };
}
